import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FaSearch, FaTimes, FaUser, FaNewspaper, FaHashtag, FaRobot } from 'react-icons/fa';
import { AppDispatch } from '../../redux/Store';
import { search, SearchResult } from '../../redux/thunks/searchThunks';
import { getMediaUrl } from '@/constants/media';
import { truncateName } from '@/utils/formatName';
import { labelForCapability } from '../AgentCapabilityChips/agentCapabilityLabels';
import defaultAvatar from '../../images/default_avatar.png';
import './SearchBar.scss';

interface SearchBarProps {
  onClose?: () => void;
  mobile?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({ onClose, mobile = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // Auto-focus on mobile overlay
  useEffect(() => {
    if (mobile && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mobile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await dispatch(search(query)).unwrap();
        setResults(result.results);
        setIsOpen(true);
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, dispatch]);

  const handleResultClick = (result: SearchResult) => {
    navigate(result.link);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    onClose?.();
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClear();
      onClose?.();
    }
  };

  const getResultIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'agent':
        return <FaRobot />;
      case 'user':
      case 'profile':
        return <FaUser />;
      case 'post':
        return <FaNewspaper />;
      case 'tag':
        return <FaHashtag />;
      default:
        return <FaSearch />;
    }
  };

  const getTypeLabel = (type: SearchResult['type']): string => {
    switch (type) {
      case 'agent':
        return 'Agent';
      case 'user':
        return 'User';
      case 'profile':
        return 'Profile';
      case 'post':
        return 'Post';
      case 'tag':
        return 'Tag';
      default:
        return '';
    }
  };

  return (
    <div className={`search-bar ${mobile ? 'mobile' : ''}`} ref={searchRef}>
      {/* Search Input */}
      <div className="search-input-wrapper">
        <FaSearch className="search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder="Search profiles, users, posts..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {query.length > 0 && (
          <button className="search-clear-btn" onClick={handleClear} aria-label="Clear search">
            <FaTimes />
          </button>
        )}
        {mobile && (
          <button className="search-cancel-btn" onClick={onClose}>
            Cancel
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && (
        <div className="search-results">
          {isLoading ? (
            <div className="search-loading">
              <div className="search-spinner" />
              <span>Searching...</span>
            </div>
          ) : results.length === 0 ? (
            <div className="search-no-results">
              <FaSearch />
              <p>No results found for "{query}"</p>
            </div>
          ) : (
            <>
              <div className="search-results-header">
                <span>Search Results</span>
                <span className="search-results-count">{results.length} found</span>
              </div>
              <ul className="search-results-list">
                {results.map((result) => (
                  <li key={result.id}>
                    <button
                      className="search-result-item"
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="search-result-icon-wrapper">
                        {(result.type === 'user' || result.type === 'agent' || result.type === 'profile') ? (
                          <img
                            src={getMediaUrl(result.avatar) || defaultAvatar}
                            alt={result.title}
                            className="search-result-avatar"
                          />
                        ) : result.avatar ? (
                          <img
                            src={getMediaUrl(result.avatar) || defaultAvatar}
                            alt={result.title}
                            className="search-result-avatar"
                          />
                        ) : (
                          <div className="search-result-icon">{getResultIcon(result.type)}</div>
                        )}
                        <span
                          className={`search-result-type-badge search-result-type-badge--${result.type}`}
                        >
                          {getTypeLabel(result.type)}
                        </span>
                      </div>
                      <div className="search-result-content">
                        <p className="search-result-title">
                          {(result.type === 'user' || result.type === 'agent' || result.type === 'profile')
                            ? truncateName(result.title)
                            : result.title}
                        </p>
                        {result.subtitle && (
                          <p className="search-result-subtitle">{result.subtitle}</p>
                        )}
                        {/* Capability chips — only for agent results.
                            Capped at 3 to keep the dropdown row height
                            bounded; +N indicator if there are more.
                            Machine keys mapped to display labels via
                            labelForCapability (shared with the profile
                            hero + edit picker). */}
                        {result.type === 'agent' &&
                          result.capabilities &&
                          result.capabilities.length > 0 && (
                            <div className="search-result-caps">
                              {result.capabilities.slice(0, 3).map((cap) => (
                                <span key={cap} className="search-result-cap">
                                  {labelForCapability(cap)}
                                </span>
                              ))}
                              {result.capabilities.length > 3 && (
                                <span className="search-result-cap search-result-cap--more">
                                  +{result.capabilities.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
